import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import apisecured from "../../../api/apisecured";
import APIDASH from "../../../api/apidash";
import { setLogOut } from "../../../State/authSlicer";
import { useDispatch, useSelector } from "react-redux";
import DiscardModal from "../../../components/Dashcomponents/New/DiscardModal";
import {
  setNotificationCount,
  incrementNotificationCount,
} from "../../../State/notificationSlice";

/* ─────────────────────────────────────────
   CSS Variables + styles that Tailwind can't express
───────────────────────────────────────── */
const minimalStyles = `
  :root {
    --bg-primary: #030021;
    --bg-card: rgba(255,255,255,0.03);
    --bg-card-hover: rgba(255,255,255,0.05);
    --bg-elevated: rgba(255,255,255,0.06);
    --bg-input: rgba(255,255,255,0.04);
    --bg-input-focus: rgba(255,255,255,0.08);
    --bg-dropdown: rgba(3,0,33,0.95);
    --primary: #00FF8F;
    --primary-dim: rgba(0,255,143,0.1);
    --primary-glow: rgba(0,255,143,0.3);
    --yellow: #fbbf24;
    --yellow-dim: rgba(251,191,36,0.1);
    --coral: #f472b6;
    --coral-dim: rgba(244,114,182,0.1);
    --purple: #a855f7;
    --purple-dim: rgba(168,85,247,0.1);
    --cyan: #22d3d8;
    --danger: #ef4444;
    --danger-dim: rgba(239,68,68,0.1);
    --text-primary: #ffffff;
    --text-secondary: rgba(255,255,255,0.6);
    --text-tertiary: rgba(255,255,255,0.4);
    --text-placeholder: rgba(255,255,255,0.3);
    --border: rgba(255,255,255,0.08);
    --border-hover: rgba(255,255,255,0.15);
    --header-height: 60px;
    --sidebar-width: 260px;
  }

  @keyframes sp-spin { to { transform: rotate(360deg); } }

  .sp-notif-list::-webkit-scrollbar { width: 5px; }
  .sp-notif-list::-webkit-scrollbar-track { background: transparent; }
  .sp-notif-list::-webkit-scrollbar-thumb { background: var(--border); border-radius: 3px; }
  .sp-notif-list::-webkit-scrollbar-thumb:hover { background: var(--border-hover); }

  .sp-notif-spinner {
    animation: sp-spin 0.7s linear infinite;
  }

  .sp-sidebar-transition {
    transition: transform 0.3s cubic-bezier(0.4,0,0.2,1);
  }

  @media (max-width: 480px) {
    .sp-notif-panel-mobile {
      position: fixed !important;
      top: 60px !important;
      left: 0 !important;
      right: 0 !important;
      width: 100% !important;
      max-height: calc(100vh - 60px) !important;
      border-radius: 0 !important;
    }
  }

  @media (min-width: 1024px) {
    .sp-sidebar-desktop {
      transform: translateX(0) !important;
      top: 60px !important;
    }
    .sp-sidebar-overlay-hide { display: none !important; }
    .sp-hamburger-hide { display: none !important; }
    .sp-main-content { margin-left: 260px; }
  }

  @media (max-width: 1023px) {
    .sp-header-nav-hide { display: none !important; }
  }

  @media (max-width: 600px) {
    .sp-creator-mode-hide { display: none !important; }
  }

  .confirm-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(3,0,33,0.85);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s ease;
  }

  .confirm-modal-overlay.visible {
    opacity: 1;
    visibility: visible;
  }

  .confirm-modal {
    background: rgba(3,0,33,0.95);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 16px;
    padding: 24px;
    max-width: 400px;
    width: 90%;
    text-align: center;
    transform: scale(0.95);
    transition: transform 0.2s ease;
  }

  .confirm-modal-overlay.visible .confirm-modal {
    transform: scale(1);
  }

  .confirm-modal-title {
    font-size: 18px;
    font-weight: 600;
    color: #fff;
    margin: 0 0 8px 0;
  }

  .confirm-modal-message {
    font-size: 14px;
    color: rgba(255,255,255,0.6);
    margin: 0 0 24px 0;
    line-height: 1.5;
  }

  .confirm-modal-buttons {
    display: flex;
    gap: 12px;
    justify-content: center;
  }

  .confirm-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 18px;
    border-radius: 10px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    border: none;
    flex: 1;
    max-width: 140px;
  }

  .confirm-btn-secondary {
    background: rgba(255,255,255,0.03);
    color: #fff;
    border: 1px solid rgba(255,255,255,0.08);
  }

  .confirm-btn-secondary:hover {
    background: rgba(255,255,255,0.05);
    border-color: rgba(255,255,255,0.15);
  }

  .confirm-btn-primary {
    background: #00FF8F;
    color: #030021;
  }

  .confirm-btn-primary:hover {
    background: #00e680;
    box-shadow: 0 0 20px rgba(0,255,143,0.4);
  }
`;

/* ─────────────────────────────────────────
   SVGs
───────────────────────────────────────── */
const LogoSvg = ({ idPrefix = "h" }) => (
  <svg className="w-8 h-8" viewBox="375 478 215 245">
    <defs>
      <linearGradient
        id={`${idPrefix}g1`}
        gradientUnits="userSpaceOnUse"
        x1="393.67"
        y1="684.95"
        x2="533.85"
        y2="544.05"
      >
        <stop offset="0" stopColor="#EF237C" />
        <stop offset="1" stopColor="#FFD50E" />
      </linearGradient>
      <linearGradient
        id={`${idPrefix}g2`}
        gradientUnits="userSpaceOnUse"
        x1="440.92"
        y1="745.23"
        x2="552.7"
        y2="633.44"
      >
        <stop offset="0" stopColor="#EF237C" />
        <stop offset="1" stopColor="#FFD50E" />
      </linearGradient>
      <linearGradient
        id={`${idPrefix}g3`}
        gradientUnits="userSpaceOnUse"
        x1="422.41"
        y1="705.54"
        x2="515.05"
        y2="625.48"
      >
        <stop offset="0" stopColor="#EF237C" />
        <stop offset="1" stopColor="#FFD50E" />
      </linearGradient>
      <linearGradient
        id={`${idPrefix}g4`}
        gradientUnits="userSpaceOnUse"
        x1="523.51"
        y1="453.91"
        x2="392.39"
        y2="584.13"
      >
        <stop offset="0" stopColor="#00A1FF" />
        <stop offset="1" stopColor="#00FF8F" />
      </linearGradient>
      <linearGradient
        id={`${idPrefix}g5`}
        gradientUnits="userSpaceOnUse"
        x1="541.26"
        y1="501.88"
        x2="417.71"
        y2="676.55"
      >
        <stop offset="0" stopColor="#00A1FF" />
        <stop offset="1" stopColor="#00FF8F" />
      </linearGradient>
      <linearGradient
        id={`${idPrefix}g6`}
        gradientUnits="userSpaceOnUse"
        x1="543.31"
        y1="488.63"
        x2="455.67"
        y2="569.01"
      >
        <stop offset="0" stopColor="#00A1FF" />
        <stop offset="1" stopColor="#00FF7B" />
      </linearGradient>
    </defs>
    <path
      fill={`url(#${idPrefix}g1)`}
      d="M572.12,577.15L407.87,717.12l67.91-186.27h15.84c9.4,0,18.48,1.39,27.04,3.99C541.36,541.72,560.4,557.04,572.12,577.15z"
    />
    <path
      fill={`url(#${idPrefix}g2)`}
      d="M584.74,623.98c0,0.28,0,0.56-0.01,0.83c-0.45,51.05-41.97,92.3-93.13,92.3h-83.74l164.25-139.97c0.97,1.66,1.89,3.35,2.76,5.08C581.19,594.79,584.74,608.97,584.74,623.98z"
    />
    <path
      fill={`url(#${idPrefix}g3)`}
      d="M407.87,717.12c6.74-5.95,13.55-11.83,20.32-17.75l20.41-17.64c6.79-5.89,13.64-11.71,20.47-17.57l20.49-17.54l20.57-17.45c6.86-5.81,13.7-11.65,20.59-17.42l20.65-17.35c6.92-5.75,13.8-11.53,20.75-17.24c-6.74,5.95-13.54,11.83-20.31,17.75l-20.41,17.64c-6.79,5.9-13.65,11.71-20.47,17.57l-20.49,17.54l-20.57,17.45c-6.86,5.81-13.7,11.65-20.59,17.42l-20.65,17.35C421.7,705.62,414.81,711.4,407.87,717.12z"
    />
    <path
      fill={`url(#${idPrefix}g4)`}
      d="M555.4,482.88L391.14,622.85c-0.97-1.66-1.89-3.35-2.76-5.08c-6.31-12.56-9.87-26.74-9.87-41.76c0-0.28,0-0.56,0.01-0.83c0.45-51.05,41.97-92.3,93.13-92.3H555.4z"
    />
    <path
      fill={`url(#${idPrefix}g5)`}
      d="M555.4,482.88l-67.91,186.27h-15.84c-9.4,0-18.48-1.39-27.04-3.99c-22.72-6.88-41.75-22.2-53.48-42.31L555.4,482.88z"
    />
    <path
      fill={`url(#${idPrefix}g6)`}
      d="M555.4,482.88c-6.74,5.95-13.55,11.83-20.32,17.75l-20.41,17.64c-6.79,5.89-13.64,11.71-20.47,17.57l-20.49,17.54l-20.57,17.45c-6.86,5.81-13.7,11.65-20.59,17.42l-20.66,17.35c-6.92,5.75-13.8-11.53-20.75,17.24c6.74-5.95,13.54-11.83,20.32-17.75l20.41-17.64c6.79-5.9,13.65-11.71,20.47-17.57l20.49-17.54l20.57-17.45c6.86-5.81,13.7-11.65,20.59-17.42l20.65-17.35C541.57,494.38,548.46,488.6,555.4,482.88z"
    />
  </svg>
);

const AvatarSvg = ({ initials = "VC" }) => (
  <svg viewBox="0 0 36 36">
    <defs>
      <linearGradient id="sp-avatarGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1a1a3e" />
        <stop offset="100%" stopColor="#0d0d2a" />
      </linearGradient>
    </defs>
    <circle cx="18" cy="18" r="18" fill="url(#sp-avatarGrad)" />
    <text
      x="18"
      y="23"
      textAnchor="middle"
      fill="#00FF8F"
      fontFamily="DM Sans"
      fontSize="13"
      fontWeight="700"
    >
      {initials}
    </text>
  </svg>
);

/* ─────────────────────────────────────────
   Guarded internal nav link
───────────────────────────────────────── */
function GuardedNavLink({
  to,
  className,
  children,
  onNavigateAttempt,
  end = false,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = end
    ? location.pathname === to
    : location.pathname.startsWith(to);

  return (
    <a
      href={to}
      className={
        typeof className === "function" ? className({ isActive }) : className
      }
      onClick={(e) => {
        e.preventDefault();
        if (location.pathname === to) return;
        onNavigateAttempt(to, () => navigate(to));
      }}
    >
      {children}
    </a>
  );
}

function GuardedLink({ to, className, children, onClick, onNavigateAttempt }) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <a
      href={to}
      className={className}
      onClick={(e) => {
        e.preventDefault();
        onClick?.(e);
        if (location.pathname === to) return;
        onNavigateAttempt(to, () => navigate(to));
      }}
    >
      {children}
    </a>
  );
}

/* ─────────────────────────────────────────
   NewDashHeader
───────────────────────────────────────── */
function NewDashHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const userlogin = useSelector((state) => state.auth);

  const isBaseUrl = location.pathname === "/";

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [notifOpen, setNotifOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const notifListRef = useRef(null);
  const previousScrollTop = useRef(0);
  // const notificationCount = useSelector((state) => state.notifications.count);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const pendingNavigationRef = useRef(null);

  const headerRef = useRef(null);
  const sidebarRef = useRef(null);
  const hamburgerRef = useRef(null);
  const dispatch = useDispatch();

  const closeAll = useCallback(() => {
    setNotifOpen(false);
    setUserOpen(false);
  }, []);
  useEffect(() => {
    setSidebarOpen(!isBaseUrl);
  }, [isBaseUrl]);
  useEffect(() => {
    const handler = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target))
        closeAll();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [closeAll]);

  useEffect(() => {
    if (!sidebarOpen) return;

    const handler = (e) => {
      const clickedInsideSidebar = sidebarRef.current?.contains(e.target);
      const clickedHamburger = hamburgerRef.current?.contains(e.target);

      if (!clickedInsideSidebar && !clickedHamburger) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [sidebarOpen]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") {
        closeAll();
        setSidebarOpen(false);
        setShowUnsavedModal(false);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [closeAll]);

  // In NewDashHeader — replace the existing sp-unsaved-changes useEffect
  useEffect(() => {
    const handler = (e) => {
      setHasUnsavedChanges(!!e.detail?.dirty);
    };

    window.addEventListener("sp-unsaved-changes", handler);

    // Ask the page to re-broadcast its current dirty state on mount
    window.dispatchEvent(new CustomEvent("sp-request-unsaved-state"));

    return () => window.removeEventListener("sp-unsaved-changes", handler);
  }, []);

  const handleNavigationAttempt = (path, navigateFn) => {
    if (!hasUnsavedChanges) {
      navigateFn();
      return;
    }

    if (path === location.pathname) return;

    pendingNavigationRef.current = navigateFn;
    setShowUnsavedModal(true);
    closeAll();
    setSidebarOpen(false);
  };

  const handleStayOnPage = () => {
    pendingNavigationRef.current = null;
    setShowUnsavedModal(false);
  };

  const handleLeavePage = () => {
    const next = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    setShowUnsavedModal(false);

    window.dispatchEvent(new CustomEvent("sp-force-discard-unsaved-changes"));

    if (next) next();
  };

  const handleSignOut = async () => {
    try {
      await apisecured.post("account/logout");
      await dispatch(setLogOut());
      navigate("/account", { replace: true });
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const fetchNotifications = useCallback(
    async (newPage = 1, forceUpdate = false) => {
      if (
        !forceUpdate &&
        (loading ||
          (notifications.length >= totalNotifications && newPage !== 1))
      )
        return;
      setLoading(true);
      try {
        const response = await apisecured.get(
          `account/get-notifications?page=${newPage}&pageSize=5`,
        );
        const newNotifications = response.data.notifications;
        if (!newNotifications || newNotifications.length === 0) return;
        setNotifications((prev) =>
          newPage === 1 ? newNotifications : [...prev, ...newNotifications],
        );
        setTotalNotifications(response.data.total);
        const unread = newNotifications.filter((n) => !n.isRead).length;
        setUnreadCount(unread);
        dispatch(setNotificationCount(unread));
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setTimeout(() => setLoading(false), 2000);
      }
    },
    [loading, notifications.length, totalNotifications, dispatch],
  );
  const fetchNotificationsRef = useRef(fetchNotifications);
  useEffect(() => {
    fetchNotificationsRef.current = fetchNotifications;
  }, [fetchNotifications]);

  useEffect(() => {
    if (userlogin?.uid) fetchNotificationsRef.current(1, true);
  }, [userlogin?.uid]);

  useEffect(() => {
    if (page > 1 && notifications.length < totalNotifications)
      fetchNotificationsRef.current(page);
  }, [page, notifications.length, totalNotifications]);

  useEffect(() => {
    if (!userlogin?.uid) return;
    const ws = new WebSocket("wss://api.sponsa.app/ws");

    ws.onopen = () => {
      console.log("✅ Connected to WebSocket for notifications");
      ws.send(JSON.stringify({ type: "subscribe", userUID: userlogin.uid }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("📩 WebSocket message received:", data);
        if (data.type === "NEW_NOTIFICATION") {
          console.log("🔔 New notification received, refreshing...");
          fetchNotificationsRef.current(1, true);
          setUnreadCount((prev) => prev + 1);
          dispatch(incrementNotificationCount());
        }
      } catch (e) {
        console.error("WebSocket parse error", e);
      }
    };

    ws.onerror = (err) => {
      console.error("❌ WebSocket error:", err);
    };

    ws.onclose = () => {
      console.log("🔌 WebSocket disconnected");
    };

    return () => {
      if (
        ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING
      ) {
        ws.close();
      }
    };
  }, [userlogin?.uid, dispatch]);
  const markAllNotificationsAsRead = async () => {
    try {
      await apisecured.post("account/mark-all-as-read");
      setUnreadCount(0);
      dispatch(setNotificationCount(0));
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };
  const markNotificationAsRead = async (notificationUid) => {
    const target = notifications.find((n) => n.uid === notificationUid);

    // Already read, no need to call API or update counts
    if (!target || target.isRead) return;

    // Optimistic UI update
    setNotifications((prev) =>
      prev.map((n) => (n.uid === notificationUid ? { ...n, isRead: true } : n)),
    );

    setUnreadCount((prev) => {
      const next = Math.max(prev - 1, 0);
      dispatch(setNotificationCount(next));
      return next;
    });

    try {
      await apisecured.post(
        `account/mark-notification-as-read/${notificationUid}`,
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);

      // Roll back if API fails
      setNotifications((prev) =>
        prev.map((n) =>
          n.uid === notificationUid ? { ...n, isRead: false } : n,
        ),
      );

      setUnreadCount((prev) => {
        const next = prev + 1;
        dispatch(setNotificationCount(next));
        return next;
      });
    }
  };
  const handleScroll = () => {
    if (!notifListRef.current || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = notifListRef.current;
    if (
      scrollTop > previousScrollTop.current &&
      scrollTop + clientHeight >= scrollHeight - 60
    ) {
      if (!loading && notifications.length < totalNotifications) {
        setPage((prev) => prev + 1);
      }
    }
    previousScrollTop.current = scrollTop;
  };

  const truncateWords = (text, maxWords = 10) => {
    if (!text) return "";
    const words = text.trim().split(/\s+/);
    return words.length <= maxWords
      ? text
      : words.slice(0, maxWords).join(" ") + "...";
  };
  const isLoggedIn = userlogin?.isAuthenticated;
  const isCreator = Number(userlogin?.accountType) !== 2;
  const isSupporter = userlogin?.accountType === 2;
  const showSidebar = isLoggedIn && isCreator;
  const showDesktopSidebar = showSidebar && !isBaseUrl;
  const username = userlogin?.sponsaUserName ?? "";
  const displayName = userlogin?.displayName ?? "";
  const handle = username ? `@${username}` : "@";
  const initials =
    displayName
      .split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "VC";

  return (
    <>
      <style>{minimalStyles}</style>

      <header
        ref={headerRef}
        className="fixed top-0 left-0 right-0 h-[60px] bg-[rgba(3,0,33,0.95)] backdrop-blur-xl border-b border-[rgba(255,255,255,0.08)] flex items-center justify-between px-5 z-[1000]"
        style={{ WebkitBackdropFilter: "blur(20px)" }}
      >
        <div className="flex items-center gap-4 max-[600px]:gap-[10px]">
          <button
            ref={hamburgerRef}
            className="sp-hamburger-hide w-9 h-9 border-none bg-transparent text-white cursor-pointer flex items-center justify-center rounded-lg hover:bg-[rgba(255,255,255,0.06)] transition-colors duration-200"
            aria-label="Open menu"
            onClick={() => setSidebarOpen((v) => !v)}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>

          <Link to={"/"} className="flex items-center gap-[10px] no-underline">
            <LogoSvg idPrefix="hdr" />
            <span className="text-lg font-bold text-white tracking-tight">
              SPONSA
            </span>
            <span className="text-[9px] font-bold bg-[#00FF8F] text-[#030021] px-[6px] py-1 rounded uppercase tracking-[0.05em] -ml-1 -mt-3 inline-flex items-center justify-center leading-none">
              BETA
            </span>
          </Link>
        </div>

        <div className="flex items-center gap-3 max-[600px]:gap-2">
          {isCreator && (
            <div className="sp-creator-mode-hide flex items-center gap-2 px-[14px] py-[6px] rounded-[20px] border border-[#00FF8F] text-[#00FF8F] text-xs font-semibold pointer-events-none select-none">
              <span className="w-[7px] h-[7px] rounded-full bg-[#00FF8F]" />
              Creator Mode
            </div>
          )}

          {isSupporter && (
            <div className="sp-creator-mode-hide flex items-center gap-2 px-[14px] py-[6px] rounded-[20px] border border-[#00FF8F] text-[#00FF8F] text-xs font-semibold pointer-events-none select-none">
              <span className="w-[7px] h-[7px] rounded-full bg-[#00FF8F]" />
              Supporter Mode
            </div>
          )}

          <nav className="sp-header-nav-hide flex items-center gap-1">
            <GuardedNavLink
              to="/explore"
              onNavigateAttempt={handleNavigationAttempt}
              className={({ isActive }) =>
                `text-[13px] font-medium no-underline px-[14px] py-2 rounded-lg transition-all duration-200 ${
                  isActive
                    ? "text-[#00FF8F]"
                    : "text-[rgba(255,255,255,0.6)] hover:text-white hover:bg-[rgba(255,255,255,0.06)]"
                }`
              }
            >
              Explore
            </GuardedNavLink>

            {isCreator && (
              <GuardedNavLink
                to={`/${username}/current-goals`}
                onNavigateAttempt={handleNavigationAttempt}
                className={({ isActive }) =>
                  `text-[13px] font-medium no-underline px-[14px] py-2 rounded-lg transition-all duration-200 ${
                    isActive
                      ? "text-[#00FF8F]"
                      : "text-[rgba(255,255,255,0.6)] hover:text-white hover:bg-[rgba(255,255,255,0.06)]"
                  }`
                }
              >
                My Page
              </GuardedNavLink>
            )}
          </nav>

          {isCreator && (
            <div className="relative">
              <button
                className="w-9 h-9 border-none bg-transparent text-[rgba(255,255,255,0.6)] cursor-pointer flex items-center justify-center rounded-lg transition-all duration-200 hover:text-white hover:bg-[rgba(255,255,255,0.06)]"
                aria-label="Notifications"
                onClick={(e) => {
                  e.stopPropagation();
                  setUserOpen(false);
                  setNotifOpen((v) => {
                    if (!v) fetchNotifications(1, true);
                    return !v;
                  });
                }}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 min-w-[16px] h-4 px-1 rounded-lg bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>

              <div
                className={`sp-notif-panel-mobile absolute top-[calc(100%+8px)] right-0 w-[380px] max-h-[480px] bg-[rgba(3,0,33,0.95)] border border-[rgba(255,255,255,0.08)] rounded-2xl flex flex-col overflow-hidden z-[1001] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.5)] transition-all duration-200 ${notifOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-[10px]"}`}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="px-4 py-4 border-b border-[rgba(255,255,255,0.08)] flex items-center justify-between flex-shrink-0">
                  <span className="text-sm font-semibold text-white">
                    Notifications
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      className="text-xs text-[#00FF8F] bg-none border-none cursor-pointer font-medium hover:underline"
                      onClick={markAllNotificationsAsRead}
                    >
                      Mark all read
                    </button>
                    <button
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-[rgba(255,255,255,0.4)] transition-all duration-150 bg-none border-none cursor-pointer hover:bg-[rgba(255,255,255,0.06)] hover:text-white"
                      title="Notification Settings"
                      onClick={() => {
                        closeAll();
                        handleNavigationAttempt("/notifications-setting", () =>
                          navigate("/notifications-setting"),
                        );
                      }}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* List */}
                <div
                  ref={notifListRef}
                  onScroll={handleScroll}
                  className="sp-notif-list overflow-y-auto flex-1 max-h-[380px]"
                >
                  {notifications.filter(
                    (n) =>
                      n.category === "GoalTips" || n.category === "GoalFunded",
                  ).length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-[10px] px-6 py-12 text-center text-[rgba(255,255,255,0.4)]">
                      <svg
                        className="w-10 h-10 opacity-40"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                        />
                      </svg>
                      <div className="text-sm font-semibold text-[rgba(255,255,255,0.6)]">
                        No notifications yet
                      </div>
                      <div className="text-xs text-[rgba(255,255,255,0.4)] leading-relaxed">
                        When supporters tip your goals, you'll see it here.
                      </div>
                    </div>
                  ) : (
                    notifications
                      .filter(
                        (n) =>
                          n.category === "GoalTips" ||
                          n.category === "GoalFunded",
                      )
                      .map((n) => {
                        const currencySymbol =
                          n.currency === "GBP"
                            ? "£"
                            : n.currency === "USD"
                              ? "$"
                              : (n.currency ?? "");
                        const isUnread = !n.isRead;

                        return (
                          <a
                            key={n.uid}
                            href={`/${n.url}`}
                            className={`px-4 py-[14px] flex gap-3 border-b border-[rgba(255,255,255,0.08)] transition-colors duration-150 cursor-pointer no-underline text-inherit ${
                              isUnread
                                ? "bg-[rgba(0,255,143,0.08)] hover:bg-[rgba(0,255,143,0.13)]"
                                : "hover:bg-[rgba(255,255,255,0.04)]"
                            }`}
                            onClick={(e) => {
                              e.preventDefault();

                              if (!n.isRead) {
                                markNotificationAsRead(n.uid);
                              }

                              handleNavigationAttempt(`/${n.url}`, () =>
                                navigate(`/${n.url}`),
                              );
                            }}
                          >
                            {/* Icon */}
                            <div
                              className={`w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0 ${
                                n.category === "GoalFunded"
                                  ? "bg-[rgba(251,191,36,0.1)] text-[#fbbf24]"
                                  : "bg-[rgba(0,255,143,0.1)] text-[#00FF8F]"
                              }`}
                            >
                              {n.category === "GoalFunded" ? (
                                <svg
                                  className="w-[18px] h-[18px]"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  className="w-[18px] h-[18px]"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                              )}
                            </div>

                            {/* Text */}
                            <div className="flex-1 min-w-0">
                              <div className="text-[13px] text-white leading-[1.4]">
                                {n.category === "GoalTips" ? (
                                  <>
                                    <strong>{n.senderName || "Someone"}</strong>{" "}
                                    tipped{" "}
                                    <span className="text-[#00FF8F] font-bold">
                                      {currencySymbol}
                                      {parseFloat(n.amount)}
                                    </span>{" "}
                                    on your{" "}
                                    <strong>
                                      {truncateWords(n.goalTitle)}
                                    </strong>{" "}
                                    goal
                                  </>
                                ) : (
                                  <>
                                    Your{" "}
                                    <strong>
                                      {truncateWords(n.goalTitle)}
                                    </strong>{" "}
                                    goal was marked complete!
                                  </>
                                )}
                              </div>
                              <div className="text-[11px] text-[rgba(255,255,255,0.4)] mt-[2px]">
                                {new Date(n.createdAt).toLocaleDateString(
                                  "en-GB",
                                  {
                                    day: "numeric",
                                    month: "short",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  },
                                )}
                              </div>
                            </div>
                          </a>
                        );
                      })
                  )}

                  {loading && notifications.length > 0 && (
                    <div className="flex items-center justify-center gap-2 py-4">
                      <svg
                        className="sp-notif-spinner w-4 h-4 text-[#00FF8F]"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8z"
                        />
                      </svg>
                      <span className="text-[12px] text-[rgba(255,255,255,0.4)]">
                        Loading more...
                      </span>
                    </div>
                  )}

                  {!loading &&
                    notifications.length > 0 &&
                    notifications.length >= totalNotifications && (
                      <div className="flex items-center justify-center py-4">
                        <span className="text-[12px] text-[rgba(255,255,255,0.4)]">
                          You're all caught up!
                        </span>
                      </div>
                    )}
                </div>
              </div>
            </div>
          )}

          {isLoggedIn && (
            <div className="relative">
              <button
                className="w-9 h-9 rounded-full border-2 border-transparent bg-[rgba(255,255,255,0.06)] overflow-hidden cursor-pointer transition-all duration-200 p-0 hover:border-[#00FF8F]"
                onClick={(e) => {
                  e.stopPropagation();
                  setNotifOpen(false);
                  setUserOpen((v) => !v);
                }}
              >
                {userlogin?.userImg ? (
                  <img
                    src={`${APIDASH}${userlogin.userImg}`}
                    alt="profile"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <AvatarSvg initials={initials} />
                )}
              </button>

              <div
                className={`absolute top-[calc(100%+8px)] right-0 w-60 bg-[rgba(3,0,33,0.95)] border border-[rgba(255,255,255,0.08)] rounded-2xl p-2 z-[1001] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.5)] transition-all duration-200 ${userOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-[10px]"}`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-3 py-3 border-b border-[rgba(255,255,255,0.08)] mb-1">
                  <div className="text-sm font-semibold text-white">
                    {displayName}
                  </div>
                  <div className="text-xs text-[rgba(255,255,255,0.4)] mt-[2px]">
                    {handle}
                  </div>
                </div>

                {isCreator && (
                  <>
                    <GuardedLink
                      to={`/${username}/current-goals`}
                      onNavigateAttempt={handleNavigationAttempt}
                      onClick={closeAll}
                      className="flex items-center gap-[10px] px-3 py-[10px] rounded-[10px] text-[13px] font-medium text-[rgba(255,255,255,0.6)] no-underline w-full transition-all duration-150 hover:bg-[rgba(255,255,255,0.06)] hover:text-white"
                    >
                      <svg
                        className="w-[18px] h-[18px] opacity-70"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      View My Page
                    </GuardedLink>

                    <GuardedLink
                      to="/insights/"
                      onNavigateAttempt={handleNavigationAttempt}
                      onClick={closeAll}
                      className="flex items-center gap-[10px] px-3 py-[10px] rounded-[10px] text-[13px] font-medium text-[rgba(255,255,255,0.6)] no-underline w-full transition-all duration-150 hover:bg-[rgba(255,255,255,0.06)] hover:text-white"
                    >
                      <svg
                        className="w-[18px] h-[18px] opacity-70"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                        />
                      </svg>
                      Insights
                    </GuardedLink>

                    <GuardedLink
                      to="/profile-setting"
                      onNavigateAttempt={handleNavigationAttempt}
                      onClick={closeAll}
                      className="flex items-center gap-[10px] px-3 py-[10px] rounded-[10px] text-[13px] font-medium text-[rgba(255,255,255,0.6)] no-underline w-full transition-all duration-150 hover:bg-[rgba(255,255,255,0.06)] hover:text-white"
                    >
                      <svg
                        className="w-[18px] h-[18px] opacity-70"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      Settings
                    </GuardedLink>
                  </>
                )}

                <div className="h-px bg-[rgba(255,255,255,0.08)] my-1" />

                <a
                  href="https://help.sponsa.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-[10px] px-3 py-[10px] rounded-[10px] text-[13px] font-medium text-[rgba(255,255,255,0.6)] no-underline w-full transition-all duration-150 hover:bg-[rgba(255,255,255,0.06)] hover:text-white"
                  onClick={closeAll}
                >
                  <svg
                    className="w-[18px] h-[18px] opacity-70"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                  Help Desk
                </a>

                <div className="h-px bg-[rgba(255,255,255,0.08)] my-1" />

                <button
                  className="flex items-center gap-[10px] px-3 py-[10px] rounded-[10px] text-[13px] font-medium text-red-500 bg-none border-none w-full cursor-pointer font-[inherit] transition-all duration-150 hover:bg-[rgba(239,68,68,0.1)]"
                  onClick={handleSignOut}
                >
                  <svg
                    className="w-[18px] h-[18px] opacity-70"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      <div
        className={`sp-sidebar-overlay-hide fixed inset-0 bg-[rgba(3,0,33,0.7)] backdrop-blur-[4px] z-[900] transition-all duration-300 ${sidebarOpen ? "opacity-100 visible" : "opacity-0 invisible"}`}
        onClick={() => setSidebarOpen(false)}
      />

      {showSidebar && (
        <aside
          ref={sidebarRef}
          id="sp-sidebar"
          className={`${
            showDesktopSidebar ? "sp-sidebar-desktop" : ""
          } sp-sidebar-transition fixed top-0 left-0 bottom-0 w-[260px] border-r border-[rgba(255,255,255,0.08)] z-[950] flex flex-col overflow-y-auto ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
          style={{
            background:
              "linear-gradient(180deg, rgba(3,0,33,0.7) 0%, rgba(3,0,33,0.65) 100%)",
            backdropFilter: "blur(28px) saturate(1.3)",
            WebkitBackdropFilter: "blur(28px) saturate(1.3)",
          }}
        >
          <div className="lg:hidden px-5 py-4 border-b border-[rgba(255,255,255,0.08)] flex items-center justify-between">
            <Link
              to={isCreator ? `/${username}/current-goals` : "/explore"}
              className="flex items-center gap-[10px] no-underline"
            >
              <LogoSvg idPrefix="sdb" />
              <span className="text-lg font-bold text-white tracking-tight">
                SPONSA
              </span>
            </Link>
            <button
              className="w-9 h-9 border-none bg-[rgba(255,255,255,0.04)] text-[rgba(255,255,255,0.6)] rounded-[10px] cursor-pointer flex items-center justify-center transition-all duration-200 hover:bg-[rgba(255,255,255,0.06)] hover:text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="flex-1 p-3 flex flex-col gap-1">
            <nav className="mb-2">
              <div className="text-[10px] font-bold text-[rgba(255,255,255,0.4)] uppercase tracking-[0.08em] px-3 pt-3 pb-[6px]">
                Main
              </div>

              <GuardedNavLink
                to="/explore"
                onNavigateAttempt={handleNavigationAttempt}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-[10px] rounded-[10px] text-[13px] font-medium no-underline transition-all duration-150 cursor-pointer ${isActive ? "bg-[rgba(0,255,143,0.1)] text-[#00FF8F] [&>svg]:opacity-100" : "text-[rgba(255,255,255,0.6)] hover:bg-[rgba(255,255,255,0.06)] hover:text-white"}`
                }
              >
                <svg
                  className="w-[18px] h-[18px] flex-shrink-0 opacity-70"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                Explore
              </GuardedNavLink>

              <GuardedNavLink
                to={`/${username}/current-goals`}
                onNavigateAttempt={handleNavigationAttempt}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-[10px] rounded-[10px] text-[13px] font-medium no-underline transition-all duration-150 cursor-pointer ${isActive ? "bg-[rgba(0,255,143,0.1)] text-[#00FF8F] [&>svg]:opacity-100" : "text-[rgba(255,255,255,0.6)] hover:bg-[rgba(255,255,255,0.06)] hover:text-white"}`
                }
              >
                <svg
                  className="w-[18px] h-[18px] flex-shrink-0 opacity-70"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                My Page
              </GuardedNavLink>
            </nav>

            <nav className="mb-2" id="sp-creatorNav">
              <div className="text-[10px] font-bold text-[rgba(255,255,255,0.4)] uppercase tracking-[0.08em] px-3 pt-3 pb-[6px]">
                Creator Dashboard
              </div>

              <GuardedNavLink
                to="/insights/"
                onNavigateAttempt={handleNavigationAttempt}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-[10px] rounded-[10px] text-[13px] font-medium no-underline transition-all duration-150 cursor-pointer ${isActive ? "bg-[rgba(0,255,143,0.1)] text-[#00FF8F] [&>svg]:opacity-100" : "text-[rgba(255,255,255,0.6)] hover:bg-[rgba(255,255,255,0.06)] hover:text-white"}`
                }
              >
                <svg
                  className="w-[18px] h-[18px] flex-shrink-0 opacity-70"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
                Insights
              </GuardedNavLink>

              <GuardedNavLink
                to="/referral-codes"
                onNavigateAttempt={handleNavigationAttempt}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-[10px] rounded-[10px] text-[13px] font-medium no-underline transition-all duration-150 cursor-pointer ${isActive ? "bg-[rgba(0,255,143,0.1)] text-[#00FF8F] [&>svg]:opacity-100" : "text-[rgba(255,255,255,0.6)] hover:bg-[rgba(255,255,255,0.06)] hover:text-white"}`
                }
              >
                <svg
                  className="w-[18px] h-[18px] flex-shrink-0 opacity-70"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                  />
                </svg>
                Referral Codes
              </GuardedNavLink>
            </nav>

            <nav className="mb-2">
              <div className="text-[10px] font-bold text-[rgba(255,255,255,0.4)] uppercase tracking-[0.08em] px-3 pt-3 pb-[6px]">
                Settings
              </div>

              {[
                {
                  to: "/profile-setting",
                  label: "Profile",
                  icon: (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  ),
                },
                {
                  to: "/payment-setting",
                  label: "Payments",
                  icon: (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    />
                  ),
                },
                {
                  to: "/stream-setting",
                  label: "Stream Alerts",
                  icon: (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  ),
                },
                {
                  to: "/notifications-setting",
                  label: "Notifications",
                  icon: (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                    />
                  ),
                },
                {
                  to: "/chatbot-setting",
                  label: "Chatbot",
                  icon: (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  ),
                },
                {
                  to: "/integrations-setting",
                  label: "Integrations",
                  icon: (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z"
                    />
                  ),
                },
              ].map(({ to, label, icon }) => (
                <GuardedNavLink
                  key={to}
                  to={to}
                  onNavigateAttempt={handleNavigationAttempt}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-[10px] rounded-[10px] text-[13px] font-medium no-underline transition-all duration-150 cursor-pointer ${isActive ? "bg-[rgba(0,255,143,0.1)] text-[#00FF8F] [&>svg]:opacity-100" : "text-[rgba(255,255,255,0.6)] hover:bg-[rgba(255,255,255,0.06)] hover:text-white"}`
                  }
                >
                  <svg
                    className="w-[18px] h-[18px] flex-shrink-0 opacity-70"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    {icon}
                  </svg>
                  {label}
                </GuardedNavLink>
              ))}
            </nav>

            <div className="h-px bg-[rgba(255,255,255,0.08)] my-2 mx-3" />

            <nav>
              <a
                href="https://help.sponsa.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-3 py-[10px] rounded-[10px] text-[13px] font-medium text-[rgba(255,255,255,0.6)] no-underline transition-all duration-150 hover:bg-[rgba(255,255,255,0.06)] hover:text-white relative"
              >
                <svg
                  className="w-[18px] h-[18px] flex-shrink-0 opacity-70"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
                Help Desk
                <svg
                  className="w-3 h-3 ml-auto opacity-40"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            </nav>
          </div>

          <div className="p-4 border-t border-[rgba(255,255,255,0.08)]">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                {userlogin?.userImg && !imgError ? (
                  <img
                    src={`${APIDASH}${userlogin.userImg}`}
                    alt="profile"
                    className="w-full h-full object-cover"
                    onError={() => setImgError(true)}
                  />
                ) : (
                  <svg viewBox="0 0 36 36" className="w-full h-full">
                    <circle cx="18" cy="18" r="18" fill="#1a1a3e" />
                    <text
                      x="18"
                      y="23"
                      textAnchor="middle"
                      fill="#00FF8F"
                      fontFamily="DM Sans"
                      fontSize="13"
                      fontWeight="700"
                    >
                      {initials}
                    </text>
                  </svg>
                )}
              </div>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-white">
                  {displayName}
                </div>
                <div className="text-[11px] text-[rgba(255,255,255,0.4)]">
                  {handle}
                </div>
              </div>
            </div>
          </div>
        </aside>
      )}

      <DiscardModal
        open={showUnsavedModal}
        onClose={handleStayOnPage}
        onConfirm={handleLeavePage}
        title="Unsaved changes"
        description="You have unsaved changes that will be lost if you leave this page."
        confirmText="Discard & Switch"
        cancelText="Cancel"
      />
    </>
  );
}

export default NewDashHeader;
